// OrderDetails.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
    FiArrowLeft, FiUser, FiMapPin, FiClock, FiCalendar,
    FiPackage, FiPhone, FiMail, FiHome, FiAlertCircle,
    FiPrinter, FiBox, FiCreditCard, FiHash, FiTag, FiInfo
} from 'react-icons/fi';
import { BiStore, BiBuilding, BiRestaurant } from 'react-icons/bi';
import { apiCall } from '../config/api-request';

function OrderDetails() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('order_id');

    const [loading, setLoading] = useState(true);
    const [orderInfo, setOrderInfo] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [orderItems, setOrderItems] = useState([]);
    const [orderType, setOrderType] = useState(null);
    const [error, setError] = useState(null);

    // Fetch order details
    const fetchOrderDetails = async () => {
        if (!orderId) {
            setError('No order ID provided');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const result = await apiCall(
                {
                    api: 'fetch-order-info-admin',
                    order_id: orderId,
                },
                true
            );

            if (result.status === true) {
                setOrderInfo(result.order_info);
                setOrderDetails(result.order);
                setOrderItems(result.order?.items || []);

                // Determine order type
                const type = result.order_info?.type || (result.order_info?.hotel_room_code ? 'Hotel Service' : 'Food Item');
                setOrderType(type);
            } else {
                setError(result.message || "Failed to load order details");
                toast.error(result.message || "Failed to load order details");
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
            setError("Network error. Please try again.");
            toast.error("Failed to load order details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrderDetails();
    }, [orderId]);

    // Handle print invoice
    const handlePrintInvoice = () => {
        const businessName = orderType === 'Hotel Service'
            ? orderDetails?.hotel_name
            : orderDetails?.restaurant_name;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Invoice - ${orderInfo?.order_id}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 40px; }
                    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
                    .business-name { color: #d62327; font-size: 24px; margin-bottom: 5px; }
                    .order-info { margin-bottom: 30px; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
                    th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                    th { background-color: #f5f5f5; }
                    .total { text-align: right; font-size: 18px; font-weight: bold; }
                    .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="business-name">Awiss</div>
                    <h2>${businessName || 'Order Invoice'}</h2>
                    <p>Order #${orderInfo?.order_id}</p>
                </div>
                <div class="order-info">
                    <p><strong>Date:</strong> ${orderInfo?.date} ${orderInfo?.time ? new Date(parseInt(orderInfo.time) * 1000).toLocaleTimeString() : ''}</p>
                    <p><strong>Customer:</strong> ${orderInfo?.name}</p>
                    <p><strong>Status:</strong> ${orderInfo?.status}</p>
                    ${orderInfo?.hotel_room_code ? `<p><strong>Room Number:</strong> ${orderInfo.hotel_room_code}</p>` : ''}
                    ${orderInfo?.restaurant_dine_in_code ? `<p><strong>Table Number:</strong> ${orderInfo.restaurant_dine_in_code}</p>` : ''}
                </div>
                <table>
                    <thead>
                        <tr><th>Item</th><th>Quantity</th><th>Price</th><th>Total</th></tr>
                    </thead>
                    <tbody>
                        ${orderItems.map(item => `
                            <tr>
                                <td>${item.name}</td>
                                <td>${item.quantity}</td>
                                <td>₹${item.price}</td>
                                <td>₹${item.total || (item.quantity * item.price)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="total">
                    Grand Total: ₹${orderInfo?.amount}
                </div>
                <div class="footer">
                    Thank you for choosing Awiss!<br>
                    For support contact: ${orderDetails?.contact_email || 'support@awiss.me'} | ${orderDetails?.contact_mobile || '+49 178 3450727'}
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    // Format timestamp
    const formatTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(parseInt(timestamp) * 1000);
        return date.toLocaleString();
    };

    // Status badge color mapping
    const getStatusColor = (status) => {
        const colors = {
            'pending': 'bg-yellow-100 text-yellow-800',
            'Accept': 'bg-green-100 text-green-800',
            'confirmed': 'bg-blue-100 text-blue-800',
            'preparing': 'bg-purple-100 text-purple-800',
            'ready': 'bg-indigo-100 text-indigo-800',
            'completed': 'bg-green-100 text-green-800',
            'cancelled': 'bg-red-100 text-red-800',
            'reject': 'bg-red-100 text-red-800',
            'delivered': 'bg-teal-100 text-teal-800'
        };
        return colors[status?.toLowerCase()] || colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Helper to get order details rows for table
    const getOrderDetailRows = () => {
        const rows = [];

        // Basic order info
        rows.push({ name: 'Order ID', value: orderInfo?.order_id || 'N/A', icon: <FiHash className="w-4 h-4" /> });
        rows.push({ name: 'Order Type', value: orderType || orderInfo?.type || 'Standard', icon: <FiTag className="w-4 h-4" /> });
        rows.push({ name: 'Reference Code', value: orderInfo?.reference_code || 'N/A', icon: <FiBox className="w-4 h-4" /> });
        rows.push({ name: 'Status', value: orderInfo?.status || 'N/A', icon: <FiInfo className="w-4 h-4" /> });
        rows.push({ name: 'Order Date', value: orderInfo?.date || 'N/A', icon: <FiCalendar className="w-4 h-4" /> });
        rows.push({ name: 'Order Time', value: formatTime(orderInfo?.time), icon: <FiClock className="w-4 h-4" /> });
        rows.push({ name: 'Total Amount', value: `₹${orderInfo?.amount || '0'}`, icon: <FiCreditCard className="w-4 h-4" /> });

        // Room/Table info based on order type
        if (orderInfo?.hotel_room_code) {
            rows.push({ name: 'Room Number', value: orderInfo.hotel_room_code, icon: <FiHome className="w-4 h-4" /> });
        }
        if (orderInfo?.restaurant_dine_in_code) {
            rows.push({ name: 'Table Number', value: orderInfo.restaurant_dine_in_code, icon: <FiHome className="w-4 h-4" /> });
        }
        if (orderInfo?.salon_counter_code) {
            rows.push({ name: 'Counter Number', value: orderInfo.salon_counter_code, icon: <FiHome className="w-4 h-4" /> });
        }

        return rows;
    };

    // Helper to get customer details rows
    const getCustomerRows = () => {
        const rows = [];
        rows.push({ name: 'Customer Name', value: orderInfo?.name || 'N/A', icon: <FiUser className="w-4 h-4" /> });
        rows.push({ name: 'Mobile Number', value: orderInfo?.mobile || 'N/A', icon: <FiPhone className="w-4 h-4" /> });
        rows.push({ name: 'Email Address', value: orderInfo?.email || 'N/A', icon: <FiMail className="w-4 h-4" /> });
        return rows;
    };

    // Helper to get business details rows
    const getBusinessRows = () => {
        const rows = [];
        const businessName = orderType === 'Hotel Service' ? orderDetails?.hotel_name : orderDetails?.restaurant_name;
        rows.push({ name: 'Business Name', value: businessName || 'N/A', icon: orderType === 'Hotel Service' ? <BiBuilding className="w-4 h-4" /> : <BiRestaurant className="w-4 h-4" /> });
        rows.push({ name: 'Location', value: orderDetails?.location || 'N/A', icon: <FiMapPin className="w-4 h-4" /> });

        if (orderDetails?.contact_mobile) {
            rows.push({ name: 'Business Phone', value: orderDetails.contact_mobile, icon: <FiPhone className="w-4 h-4" /> });
        }
        if (orderDetails?.contact_email) {
            rows.push({ name: 'Business Email', value: orderDetails.contact_email, icon: <FiMail className="w-4 h-4" /> });
        }
        if (orderDetails?.latitude && orderDetails?.longitude) {
            rows.push({
                name: 'Coordinates',
                value: `${parseFloat(orderDetails.latitude).toFixed(4)}, ${parseFloat(orderDetails.longitude).toFixed(4)}`,
                icon: <FiMapPin className="w-4 h-4" />
            });
        }
        return rows;
    };

    // Table Row Component
    const TableRow = ({ name, value, icon }) => (
        <tr className="border-b border-gray-100 hover:bg-gray-50 transition">
            <td className="px-6 py-4 w-1/3">
                <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-brand">{icon}</span>
                    <span className="font-medium">{name}</span>
                </div>
            </td>
            <td className="px-6 py-4 text-gray-800 font-medium">
                {name === 'Status' ? (
                    <span className={`px-3 py-1 rounded-full text-sm inline-block ${getStatusColor(value)}`}>
                        {value}
                    </span>
                ) : (
                    value
                )}
            </td>
        </tr>
    );

    // Skeleton Loader
    const SkeletonLoader = () => (
        <div className="animate-pulse">
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-100">
                    <div className="h-6 bg-gray-200 rounded w-40"></div>
                </div>
                <div className="p-6">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="flex py-3 border-b border-gray-100">
                            <div className="w-1/3"><div className="h-5 bg-gray-200 rounded w-28"></div></div>
                            <div className="w-2/3"><div className="h-5 bg-gray-200 rounded w-40"></div></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">
                            <button onClick={() => navigate(-1)} className="bg-gray-100 p-2.5 rounded-lg">
                                <FiArrowLeft className="w-5 h-5" />
                            </button>
                            <span className="text-xl font-bold">Order Details</span>
                            <div className="w-10"></div>
                        </div>
                    </div>
                </header>
                <main className="pt-20 max-w-4xl mx-auto px-4 py-6">
                    <SkeletonLoader />
                </main>
            </div>
        );
    }

    if (error || !orderInfo) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">
                            <button onClick={() => navigate(-1)} className="bg-gray-100 p-2.5 rounded-lg">
                                <FiArrowLeft className="w-5 h-5" />
                            </button>
                            <span className="text-xl font-bold">Order Details</span>
                            <div className="w-10"></div>
                        </div>
                    </div>
                </header>
                <main className="pt-20 max-w-4xl mx-auto px-4 py-6">
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                        <FiAlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">Order Not Found</h2>
                        <p className="text-gray-500 mb-6">{error || "Unable to load order details"}</p>
                        <button onClick={() => navigate(-1)} className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg">
                            Go Back
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    const orderDetailRows = getOrderDetailRows();
    const customerRows = getCustomerRows();
    const businessRows = getBusinessRows();

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <button
                            onClick={() => navigate(-1)}
                            className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 p-2.5 rounded-lg transition-all duration-200"
                        >
                            <FiArrowLeft className="w-5 h-5 text-gray-700" />
                        </button>
                        <div className="flex items-center gap-3">
                            <span className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                Order Details
                            </span>
                            <span className="text-xs font-mono bg-gray-100 px-3 py-1 rounded-full">
                                {orderInfo?.order_id}
                            </span>
                        </div>
                        <button
                            onClick={handlePrintInvoice}
                            className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 p-2.5 rounded-lg transition-all"
                        >
                            <FiPrinter className="w-5 h-5 text-gray-700" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20 max-w-4xl mx-auto px-4 py-6">
                {/* Order Information Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <FiInfo className="text-blue-600" />
                            Order Information
                        </h2>
                    </div>
                    <table className="w-full">
                        <tbody>
                            {orderDetailRows.map((row, index) => (
                                <TableRow key={index} name={row.name} value={row.value} icon={row.icon} />
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Customer Information Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-teal-50">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <FiUser className="text-green-600" />
                            Customer Information
                        </h2>
                    </div>
                    <table className="w-full">
                        <tbody>
                            {customerRows.map((row, index) => (
                                <TableRow key={index} name={row.name} value={row.value} icon={row.icon} />
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Business Information Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <BiStore className="text-orange-600" />
                            Business Information
                        </h2>
                    </div>
                    <table className="w-full">
                        <tbody>
                            {businessRows.map((row, index) => (
                                <TableRow key={index} name={row.name} value={row.value} icon={row.icon} />
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Order Items Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <FiPackage className="text-purple-600" />
                            Order Items ({orderItems.length})
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {orderItems.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                {item.icon && (
                                                    <img
                                                        src={item.icon}
                                                        alt={item.name}
                                                        className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                )}
                                                <span className="text-sm font-medium text-gray-900">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{item.quantity}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">₹{item.price}</td>
                                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">₹{item.total || (item.quantity * item.price)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                                <tr>
                                    <td colSpan="3" className="px-6 py-4 text-right font-bold text-gray-800">Grand Total</td>
                                    <td className="px-6 py-4 text-lg font-bold text-blue-600">₹{orderInfo?.amount}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </main>

            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: { background: '#363636', color: '#fff' }
                }}
            />
        </div>
    );
}

export default OrderDetails;